import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Button } from './ui/button';
import { Search, Users, AlertCircle } from 'lucide-react';
import { subscribeFeeDataChanged } from '../utils/feeDataEvents';
import { deriveDuesStatus } from '../utils/feeOverdueDues';
import { formatDate } from '../utils/appDateTime';
import { parseParentAccountsResponse } from '../utils/parentAccountsApi';
import { formatWithBranchLabel } from '../utils/branchLabels';
import useBranchLabels from '../hooks/useBranchLabels';

const formatStudentLabel = (student) => {
  const name = [student.first_name, student.last_name].filter(Boolean).join(' ').trim();
  const classPart = [student.class_name, student.section_name].filter(Boolean).join('-');
  if (!name) return classPart || '—';
  return classPart ? `${name} · ${classPart}` : name;
};

const ParentAccountsList = () => {
  const { user } = useAuth();
  const { showBranchLabels } = useBranchLabels();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 50, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const searchTimer = useRef(null);

  const getSelectedAcademicYearId = () => {
    const schoolId = user?.school_id;
    if (!schoolId) return null;
    return localStorage.getItem(`selected_academic_year_id_${schoolId}`);
  };

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 350);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [search]);

  const fetchAccounts = async () => {
    setLoading(true);
    setError(null);
    try {
      const academicYearId = getSelectedAcademicYearId();
      const params = {
        page,
        limit: 50,
        ...(user?.school_id ? { school_id: user.school_id } : {}),
        ...(academicYearId ? { academic_year_id: academicYearId } : {}),
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      };
      const res = await api.get('/fees/parent-accounts', { params });
      const { accounts: list, meta: nextMeta } = parseParentAccountsResponse(res.data?.data, page);
      setAccounts(list);
      setMeta(nextMeta);
    } catch (e) {
      console.error('Failed to load parent accounts', e);
      setAccounts([]);
      const status = e.response?.status;
      const apiMessage = e.response?.data?.message;
      if (status === 403) {
        setError('You do not have permission to view parent accounts.');
      } else if (status === 401) {
        setError('Session expired. Please log in again.');
      } else if (status >= 500) {
        setError(apiMessage || 'Server error loading parent accounts. Ensure database migrations are up to date.');
      } else {
        setError(apiMessage || 'Failed to load parent accounts.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, [user?.school_id, page, debouncedSearch]);

  useEffect(() => {
    const handler = () => fetchAccounts();
    window.addEventListener('academicYearChanged', handler);
    const unsubFee = subscribeFeeDataChanged((e) => {
      const sid = e.detail?.schoolId;
      if (!sid || sid === user?.school_id) fetchAccounts();
    });
    return () => {
      window.removeEventListener('academicYearChanged', handler);
      unsubFee();
    };
  }, [page, debouncedSearch, user?.school_id]);

  const formatCurrency = (n) => {
    const num = Number(n);
    if (Number.isNaN(num)) return '—';
    return `₹${num.toLocaleString('en-IN')}`;
  };

  const renderNextDue = (acc) => {
    if (!acc.next_due_date) {
      return <span className="text-muted-foreground">—</span>;
    }
    const status = deriveDuesStatus(acc.next_due_date);
    const className =
      status === 'Overdue'
        ? 'text-amber-600 dark:text-amber-400 font-medium'
        : 'text-blue-600 dark:text-blue-400 font-medium';
    return <span className={className}>{formatDate(acc.next_due_date)}</span>;
  };

  const renderStudents = (acc) => {
    const students = Array.isArray(acc.students) ? acc.students : [];
    if (students.length === 0) {
      return <span className="text-muted-foreground">—</span>;
    }
    return (
      <span className="text-foreground text-sm">
        {students.map((s, i) => (
          <span key={s.id ?? i}>
            {i > 0 ? ', ' : ''}
            {formatWithBranchLabel(formatStudentLabel(s), s.branch_name, showBranchLabels)}
          </span>
        ))}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Parent Accounts</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Centralized fee accounts by parent mobile or email. Multiple students link to one account.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <Button
            variant="outline"
            className="border-amber-600/50 text-amber-600 hover:bg-amber-600/10"
            onClick={() => navigate('/fees/accounts/dues')}
          >
            <AlertCircle className="w-4 h-4 mr-2" />
            Dues
          </Button>
        </div>
      </div>

      <Card className="border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-foreground text-lg flex items-center gap-2">
            <Users className="w-5 h-5" /> Accounts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative mb-4 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search student, parent, class, mobile..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-muted border-border"
            />
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            </div>
          ) : error ? (
            <p className="text-red-500 text-center py-8">{error}</p>
          ) : accounts.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No parent accounts found.</p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead className="text-muted-foreground">Parent</TableHead>
                    <TableHead className="text-muted-foreground">Students</TableHead>
                    <TableHead className="text-muted-foreground">Mobile</TableHead>
                    <TableHead className="text-muted-foreground">Email</TableHead>
                    <TableHead className="text-muted-foreground text-right">Yearly</TableHead>
                    <TableHead className="text-muted-foreground">Next due</TableHead>
                    <TableHead className="text-muted-foreground text-right">Pending</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {accounts.map((acc) => (
                    <TableRow
                      key={acc.id}
                      className="border-border hover:bg-muted/50 cursor-pointer"
                      tabIndex={0}
                      role="button"
                      onClick={() => navigate(`/fees/accounts/${acc.id}`)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate(`/fees/accounts/${acc.id}`);
                        }
                      }}
                    >
                      <TableCell>
                        <span className="text-blue-500 font-medium">{acc.name}</span>
                      </TableCell>
                      <TableCell>{renderStudents(acc)}</TableCell>
                      <TableCell className="text-foreground">{acc.mobile || '—'}</TableCell>
                      <TableCell className="text-muted-foreground">{acc.email || '—'}</TableCell>
                      <TableCell className="text-right text-foreground">{formatCurrency(acc.total_yearly)}</TableCell>
                      <TableCell>{renderNextDue(acc)}</TableCell>
                      <TableCell className="text-right font-medium text-amber-600 dark:text-amber-400">
                        {formatCurrency(acc.total_pending)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {meta.pages > 1 ? (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Page {meta.page} of {meta.pages} ({meta.total} accounts)
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                      Previous
                    </Button>
                    <Button variant="outline" size="sm" disabled={page >= meta.pages} onClick={() => setPage((p) => p + 1)}>
                      Next
                    </Button>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ParentAccountsList;
